import { inject, Injectable, Injector } from '@angular/core';
import { handleRequestError } from '@softline/application';
import { saveAs } from '@softline/core';
import { messageFn, modalFn, translator } from '@softline/ui-core';
import { FileViewerModal } from '@softline/ui-core/file-viewer';
import { rawDocumentId } from '../functions/document-id';
import { ArchiveDocumentRepository } from '../repositories/archive-document.repository';
import { DocumentVariant } from '../types/archive';
import { DocumentId } from '../types/document-row';

export interface ArchiveDocumentRef {
  /** Document id from the list query, prefixed or bare — see `rawDocumentId`. */
  documentId: DocumentId;
  variant: DocumentVariant;
  referenceNumber: string | null;
}

export interface ArchiveFile {
  blob: Blob;
  /** `<belegart>_<bezugsnummer>_archiv.<ext>` — the viewer's title and download name. */
  name: string;
}

/** File name pattern the portal uses: `<belegart>_<bezugsnummer>_archiv.<ext>`. */
const VARIANT_LABEL: Record<DocumentVariant, string> = {
  KOPFERW: 'Eingangsrechnung',
  KOPFEK: 'Lieferschein',
};

function extensionOf(contentType: string): string {
  if (contentType.includes('pdf')) return 'pdf';
  if (contentType.includes('xml')) return 'xml';
  return 'dat';
}

@Injectable()
export class ArchiveDocumentService {
  private readonly repository = inject(ArchiveDocumentRepository);
  private readonly injector = inject(Injector);
  private readonly message = messageFn();
  private readonly modal = modalFn();
  private readonly t = translator();

  /** The archived file of a Beleg, or null when the Belegkopf carries no fileKey. */
  async loadArchiveDocument(ref: ArchiveDocumentRef): Promise<ArchiveFile | null> {
    // The monolith's archive endpoints take the raw number. The id arrives prefixed
    // per the contract or bare from the deployed query service; both are handled.
    const rawId = rawDocumentId(ref.documentId);
    if (rawId === null) return null;
    const head = await this.repository.loadDocumentHead(rawId, ref.variant);
    const fileKey = head.result?.fileKey;
    if (!fileKey) return null;
    const blob = await this.repository.download(fileKey);
    const name = `${VARIANT_LABEL[ref.variant]}_${ref.referenceNumber ?? rawId}_archiv.${extensionOf(blob.type)}`;
    return { blob, name };
  }

  /** The modal path: no consumer today, the page reads the archive in its side panel.
   *  Kept for a surface that has no room for a second panel. */
  async openArchiveDocument(ref: ArchiveDocumentRef): Promise<void> {
    if (rawDocumentId(ref.documentId) === null) return;

    const loading = this.message.loading(this.t('#ABAU_INVOICE_LINK.OPEN_ARCHIVE'));
    let loadingOpen = true;
    // closing a message bar twice throws, and the success path closes it early
    const dismissLoading = () => {
      if (!loadingOpen) return;
      loadingOpen = false;
      loading.dismiss();
    };
    try {
      const file = await this.loadArchiveDocument(ref);
      if (!file) {
        this.message.info(this.t('#ABAU_INVOICE_LINK.MESSAGES.NO_ARCHIVE'));
        return;
      }
      // the viewer promise only settles when the modal closes
      dismissLoading();
      if (file.blob.type === 'application/pdf') {
        await this.modal({
          component: FileViewerModal,
          dismiss: true,
          class: 'wide tall',
          flushContent: true,
          title: VARIANT_LABEL[ref.variant],
          subtitle: ref.referenceNumber ?? undefined,
          icon: 'fa-regular fa-file-pdf',
          data: { file: file.blob, mimeType: 'application/pdf', fileName: file.name },
        });
      } else {
        // EDI invoices often archive the XML — only application/pdf may use the viewer
        saveAs(file.blob, file.name);
      }
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    } finally {
      dismissLoading();
    }
  }
}
